import { useMutation } from '@tanstack/react-query'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import { useSearchParams } from 'react-router'
import invariant from 'tiny-invariant'

import TextInput from '@/components/form/TextInput'
import ActionButton from '@/components/misc/ActionButton'
import { EDIT_INVITED_COMPANY_NAME_FORM_FIELDS } from '@/components/misc/EditInvitedCompanyNameButton/EditInvitedCompanyNameModal/constants'
import { EditInvitedCompanyNameFormValues } from '@/components/misc/EditInvitedCompanyNameButton/EditInvitedCompanyNameModal/types'
import {
  EDIT_INVITED_COMPANY_NAME_MODAL_SEARCH_PARAMS,
  useEditInvitedCompanyNameModal,
} from '@/components/misc/EditInvitedCompanyNameButton/EditInvitedCompanyNameModal/useEditInvitedCompanyNameModal'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useHandleApiError } from '@/hooks/useHandleApiError'
import { useInvalidatePanelInvitesQueries } from '@/hooks/useInvalidatePanelInvitesQueries'
import useNotyf from '@/hooks/useNotyf'
import panelService from '@/lib/services/panelService'

const EditInvitedCompanyNameModal: React.FC = () => {
  const notyf = useNotyf()
  const [searchParams] = useSearchParams()
  const { hideModal } = useEditInvitedCompanyNameModal()
  const companyName = searchParams.get(
    EDIT_INVITED_COMPANY_NAME_MODAL_SEARCH_PARAMS.companyName,
  )
  const source = searchParams.get(
    EDIT_INVITED_COMPANY_NAME_MODAL_SEARCH_PARAMS.source,
  )
  const sourceId = searchParams.get(
    EDIT_INVITED_COMPANY_NAME_MODAL_SEARCH_PARAMS.sourceId,
  )
  const handleApiError = useHandleApiError()
  const invalidatePanelInviteQueries = useInvalidatePanelInvitesQueries()

  const updateInvitedCompanyMutation = useMutation({
    mutationFn: async ({
      values,
    }: {
      values: EditInvitedCompanyNameFormValues
      formikHelpers: FormikHelpers<EditInvitedCompanyNameFormValues>
    }) => {
      invariant(sourceId, 'Expected sourceId to be set')

      if (!panelService.isValidPanelInviteSource(source)) {
        throw new Error('Expected `source` to be valid')
      }

      return panelService.updatePanelInvite({
        source,
        sourceId: +sourceId,
        companyName: values.companyName,
      })
    },
    onSuccess: async () => {
      await invalidatePanelInviteQueries()

      hideModal()

      notyf.success('Company name successfully updated')
    },
    onError: (error, { formikHelpers }) =>
      handleApiError({ error, formikHelpers }),
  })

  const initialValues: EditInvitedCompanyNameFormValues = {
    companyName: companyName || '',
  }

  const isProcessing = updateInvitedCompanyMutation.isPending

  if (!source || !sourceId) {
    return null
  }

  return (
    <Dialog open onHide={hideModal}>
      <DialogContent>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, formikHelpers) =>
            updateInvitedCompanyMutation.mutate({ values, formikHelpers })
          }
        >
          {({ dirty }) => (
            <Form className="flex flex-col gap-3">
              <DialogHeader>
                <DialogTitle>Edit company name</DialogTitle>
              </DialogHeader>

              <DialogBody>
                <TextInput
                  name={EDIT_INVITED_COMPANY_NAME_FORM_FIELDS.companyName}
                  autoFocus
                  placeholder="Company name"
                  isDisabled={isProcessing}
                  helpText={
                    <>
                      Please note that once the company signs up on RQ, the name
                      they choose will override your custom name.
                    </>
                  }
                />
              </DialogBody>

              <DialogFooter>
                <ActionButton
                  type="submit"
                  className="w-full sm:order-last sm:w-auto"
                  isProcessing={isProcessing}
                  isProcessingText="Updating"
                  disabled={!dirty || isProcessing}
                >
                  Update
                </ActionButton>

                <Button
                  variant="outline-secondary"
                  onClick={hideModal}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default EditInvitedCompanyNameModal
